/**
 * Multi-Server Helper Functions (Frontend)
 * Parses server_ids JSON from backend responses.
 */

/**
 * Parse server_ids from a JSON string or array to number[].
 * Handles: string (JSON), number[] (already parsed), null/undefined.
 */
export function parseServerIds(value: string | number[] | null | undefined): number[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(id => typeof id === 'number' && id > 0);
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.filter((id: unknown) => typeof id === 'number' && (id as number) > 0);
  } catch { /* invalid JSON */ }
  return [];
}

// ============================================================================
// Per-Server Proxy Helpers (Frontend)
// ============================================================================

/**
 * proxy_by_resolution per-server format:
 *   { "1": {"HD": 5, "FHD": 3}, "2": {"HD": 12} }
 *
 * Legacy single-server format (still read):
 *   { "HD": 5, "FHD": 3 }
 */
export type PerServerProxyMap = Record<string, Record<string, number | null>>;

function isPerServerFormat(obj: Record<string, any>): boolean {
  const keys = Object.keys(obj);
  if (keys.length === 0) return false;
  const first = obj[keys[0]];
  return typeof first === 'object' && first !== null && !Array.isArray(first);
}

/**
 * Parse proxy_by_resolution from DB (string or object) into per-server map.
 * Converts legacy format using primaryServerId as fallback key.
 */
export function parseProxyByResolution(
  raw: string | Record<string, any> | null | undefined,
  primaryServerId?: number | null
): PerServerProxyMap {
  if (!raw) return {};
  let obj: Record<string, any>;
  if (typeof raw === 'string') {
    try { obj = JSON.parse(raw); } catch { return {}; }
  } else {
    obj = raw;
  }
  if (Object.keys(obj).length === 0) return {};

  // Already per-server format
  if (isPerServerFormat(obj)) return obj as PerServerProxyMap;

  // Legacy format: wrap under primaryServerId
  if (primaryServerId) {
    return { [String(primaryServerId)]: obj as Record<string, number | null> };
  }
  return {};
}

/**
 * Get proxy ID for a specific resolution on a specific server.
 */
export function getProxyForServer(map: PerServerProxyMap, serverId: number, resName: string): number | null {
  const serverMap = map[String(serverId)];
  if (!serverMap) return null;
  return serverMap[resName] ?? null;
}
